import { supabase } from '../lib/supabase';

// Rendimiento thresholds — rango óptimo 40–50%
export const RENDIMIENTO_OBJETIVO = 50; // 50% upper optimal
export const RENDIMIENTO_MEDIO_MIN = 40; // 40% lower optimal

export const getRendimientoStatus = (rendimiento) => {
  if (rendimiento === null || rendimiento === undefined) return null;
  if (rendimiento > RENDIMIENTO_OBJETIVO) {
    return { label: 'Superior al objetivo', color: 'success', icon: 'TrendingUp', bgClass: 'bg-green-50 border-green-200', textClass: 'text-green-700', badgeClass: 'bg-green-100 text-green-800' };
  }
  if (rendimiento >= RENDIMIENTO_MEDIO_MIN) {
    return { label: 'Rendimiento óptimo (40–50%)', color: 'warning', icon: 'CheckCircle', bgClass: 'bg-yellow-50 border-yellow-200', textClass: 'text-yellow-700', badgeClass: 'bg-yellow-100 text-yellow-800' };
  }
  return { label: 'Inferior al esperado', color: 'danger', icon: 'TrendingDown', bgClass: 'bg-red-50 border-red-200', textClass: 'text-red-700', badgeClass: 'bg-red-100 text-red-800' };
};

export const calcularMerma = (pesoInicial, pesoAprovechado) => {
  const pi = parseFloat(pesoInicial);
  const pa = parseFloat(pesoAprovechado);
  if (isNaN(pi) || isNaN(pa) || pi <= 0) return null;
  return Math.max(0, pi - pa);
};

export const calcularRendimiento = (pesoInicial, pesoAprovechado) => {
  const pi = parseFloat(pesoInicial);
  const pa = parseFloat(pesoAprovechado);
  if (isNaN(pi) || isNaN(pa) || pi <= 0) return null;
  return Math.min(100, Math.max(0, (pa / pi) * 100));
};

export const rendimientoService = {
  // Update rendimiento data for a loncheado
  async updateRendimiento(id, { pesoInicial, pesoAprovechado }) {
    const merma = calcularMerma(pesoInicial, pesoAprovechado);
    const rendimiento = calcularRendimiento(pesoInicial, pesoAprovechado);

    const { data, error } = await supabase
      ?.from('loncheados')
      ?.update({
        peso_inicial: pesoInicial ? parseFloat(pesoInicial) : null,
        peso_aprovechado: pesoAprovechado ? parseFloat(pesoAprovechado) : null,
        merma: merma,
        rendimiento: rendimiento ? parseFloat(rendimiento?.toFixed(2)) : null
      })
      ?.eq('id', id)
      ?.select()
      ?.single();

    if (error) throw error;
    return data;
  },

  // Upload a photo for a loncheado piece — uses 'jamones' bucket
  async uploadFoto(loncheadoId, file) {
    const timestamp = Date.now();
    const ext = file?.name?.split('.')?.pop() || 'jpg';
    const filePath = `${loncheadoId}/${timestamp}.${ext}`;

    const { error: uploadError } = await supabase?.storage
      ?.from('jamones')
      ?.upload(filePath, file, { cacheControl: '3600', upsert: false });

    if (uploadError) throw uploadError;

    const { data: urlData } = supabase?.storage
      ?.from('jamones')
      ?.getPublicUrl(filePath);

    return { filePath, publicUrl: urlData?.publicUrl };
  },

  // Delete a photo from storage
  async deleteFoto(filePath) {
    const { error } = await supabase?.storage
      ?.from('jamones')
      ?.remove([filePath]);
    if (error) throw error;
  },

  // Save photo URL to loncheado record (stores single URL string or JSON array)
  async saveFotoUrl(loncheadoId, fotoUrls) {
    const fotoValue = Array.isArray(fotoUrls) && fotoUrls?.length === 1
      ? fotoUrls?.[0]
      : JSON.stringify(fotoUrls);

    const { data, error } = await supabase
      ?.from('loncheados')
      ?.update({ foto_url: fotoValue })
      ?.eq('id', loncheadoId)
      ?.select()
      ?.single();
    if (error) throw error;
    return data;
  },

  // Get loncheados with rendimiento data for analytics
  async getRendimientoAnalytics() {
    const { data, error } = await supabase
      ?.from('loncheados')
      ?.select(`
        id, rendimiento, merma, peso_inicial, peso_aprovechado, created_at,
        cliente_id, producto_id,
        clientes:cliente_id(nombre),
        productos:producto_id(nombre, categoria)
      `)
      ?.not('rendimiento', 'is', null)
      ?.order('created_at', { ascending: false });

    if (error) throw error;
    return data || [];
  },

  // Get rendimiento stats grouped by provider (cliente)
  async getRendimientoPorCliente() {
    const data = await rendimientoService?.getRendimientoAnalytics();
    const grouped = {};

    data?.forEach(item => {
      const nombre = item?.clientes?.nombre || 'Sin cliente';
      if (!grouped?.[nombre]) {
        grouped[nombre] = { nombre, rendimientos: [], mermaTotal: 0, count: 0 };
      }
      grouped?.[nombre]?.rendimientos?.push(parseFloat(item?.rendimiento) || 0);
      grouped[nombre].mermaTotal += parseFloat(item?.merma) || 0;
      grouped[nombre].count++;
    });

    return Object.values(grouped)?.map(g => ({
      nombre: g?.nombre,
      rendimientoMedio: g?.rendimientos?.length > 0
        ? parseFloat((g?.rendimientos?.reduce((a, b) => a + b, 0) / g?.rendimientos?.length)?.toFixed(2))
        : 0,
      mermaTotal: parseFloat(g?.mermaTotal?.toFixed(2)),
      count: g?.count
    }))?.sort((a, b) => b?.rendimientoMedio - a?.rendimientoMedio);
  },

  // Get rendimiento stats grouped by product type
  async getRendimientoPorTipo() {
    const data = await rendimientoService?.getRendimientoAnalytics();
    const grouped = {};

    data?.forEach(item => {
      const nombre = item?.productos?.nombre || 'Sin producto';
      if (!grouped?.[nombre]) {
        grouped[nombre] = { nombre, rendimientos: [], mermaTotal: 0, count: 0 };
      }
      grouped?.[nombre]?.rendimientos?.push(parseFloat(item?.rendimiento) || 0);
      grouped[nombre].mermaTotal += parseFloat(item?.merma) || 0;
      grouped[nombre].count++;
    });

    return Object.values(grouped)?.map(g => ({
      nombre: g?.nombre,
      rendimientoMedio: g?.rendimientos?.length > 0
        ? parseFloat((g?.rendimientos?.reduce((a, b) => a + b, 0) / g?.rendimientos?.length)?.toFixed(2))
        : 0,
      mermaTotal: parseFloat(g?.mermaTotal?.toFixed(2)),
      count: g?.count
    }))?.sort((a, b) => b?.rendimientoMedio - a?.rendimientoMedio);
  },

  // Get monthly evolution of rendimiento
  async getEvolucionMensual() {
    const data = await rendimientoService?.getRendimientoAnalytics();
    const monthNames = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];
    const grouped = {};

    data?.forEach(item => {
      const date = new Date(item?.created_at);
      const key = `${date?.getFullYear()}-${String(date?.getMonth() + 1)?.padStart(2, '0')}`;
      const label = `${monthNames?.[date?.getMonth()]} ${date?.getFullYear()}`;
      if (!grouped?.[key]) {
        grouped[key] = { key, label, rendimientos: [], mermaTotal: 0, count: 0 };
      }
      grouped?.[key]?.rendimientos?.push(parseFloat(item?.rendimiento) || 0);
      grouped[key].mermaTotal += parseFloat(item?.merma) || 0;
      grouped[key].count++;
    });

    return Object.keys(grouped)?.sort()?.map(key => ({
      mes: grouped?.[key]?.label,
      rendimientoMedio: grouped?.[key]?.rendimientos?.length > 0
        ? parseFloat((grouped?.[key]?.rendimientos?.reduce((a, b) => a + b, 0) / grouped?.[key]?.rendimientos?.length)?.toFixed(2))
        : 0,
      mermaTotal: parseFloat(grouped?.[key]?.mermaTotal?.toFixed(2)),
      count: grouped?.[key]?.count
    }));
  },

  // Get ranking of best and worst rendimientos
  async getRanking() {
    const data = await rendimientoService?.getRendimientoAnalytics();
    const sorted = [...data]?.sort((a, b) => (parseFloat(b?.rendimiento) || 0) - (parseFloat(a?.rendimiento) || 0));
    return {
      mejores: sorted?.slice(0, 5),
      peores: sorted?.slice(-5)?.reverse()
    };
  },

  // Get total accumulated merma
  async getMermaTotal() {
    const { data, error } = await supabase
      ?.from('loncheados')
      ?.select('merma')
      ?.not('merma', 'is', null);
    if (error) throw error;
    return (data || [])?.reduce((sum, item) => sum + (parseFloat(item?.merma) || 0), 0);
  }
};
